import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Calendar, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsultationSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const sessionId = searchParams.get('session_id');
  const consultationId = searchParams.get('consultation_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API}/consultations/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else {
          // Keep polling if not yet paid
          setTimeout(checkPaymentStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-gold animate-spin mx-auto mb-4" />
          <p className="text-navy text-lg">Confirming your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-cream py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl p-8 shadow-card">
            <h1 className="font-serif text-3xl text-navy mb-4">Something Went Wrong</h1>
            <p className="text-text-secondary mb-8">
              We couldn't confirm your payment. Please contact us if you believe this is an error.
            </p>
            <Link to="/consultation">
              <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-4 rounded-full">
                Try Again
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-20">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-card text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>

          <h1 className="font-serif text-3xl md:text-4xl text-navy mb-4">
            Payment Successful!
          </h1>
          
          <p className="text-text-secondary text-lg mb-8">
            Thank you for booking a consultation with Live Life Global Wellness Advisory Group.
            Your $25 consultation fee has been received and will be credited toward your travel booking.
          </p>

          {/* What's Next */}
          <div className="bg-cream rounded-2xl p-6 mb-8 text-left">
            <h2 className="font-serif text-xl text-navy mb-4">What Happens Next?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-semibold text-navy">Confirmation Email</p>
                  <p className="text-sm text-text-muted">You'll receive a confirmation email with your consultation details.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-semibold text-navy">Brandi Will Contact You</p>
                  <p className="text-sm text-text-muted">Within 24 hours, Brandi will reach out to schedule your consultation call.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center text-navy font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-semibold text-navy">Plan Your Dream Trip</p>
                  <p className="text-sm text-text-muted">During your consultation, you'll discuss your travel goals and preferences.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <div className="flex items-center justify-center gap-2 text-text-secondary">
              <Mail className="w-5 h-5 text-gold" />
              <a href="mailto:brandi@livelifeglobalwellness.com" className="hover:text-gold">
                brandi@livelifeglobalwellness.com
              </a>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button className="bg-gold hover:bg-gold-dark text-navy font-semibold px-8 py-4 rounded-full">
                Back to Home
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="border-2 border-navy text-navy hover:bg-navy hover:text-white px-8 py-4 rounded-full">
                Explore Our Services
              </Button>
            </Link>
          </div>
        </div>

        {/* Note */}
        <p className="text-center text-text-muted text-sm mt-6">
          Your $25 consultation fee will be credited toward any vacation booked through our agency.
        </p>
      </div>
    </div>
  );
};

export default ConsultationSuccessPage;
